import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Upload, Link as LinkIcon, Loader2, Play, Music, Image as ImageIcon } from 'lucide-react';
import { TikTokEmbed, InstagramEmbed, FacebookEmbed } from 'react-social-media-embed';

// Media type detection and embed URL generation
const detectMediaType = (url) => {
  if (!url) return { type: 'unknown', embedUrl: null, originalUrl: url };
  
  const urlLower = url.toLowerCase();
  
  // YouTube
  if (urlLower.includes('youtube.com/watch') || urlLower.includes('youtu.be/')) {
    const videoId = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/)?.[1];
    return { 
      type: 'youtube', 
      embedUrl: videoId ? `https://www.youtube.com/embed/${videoId}` : null,
      videoId,
      originalUrl: url
    };
  }
  
  // YouTube Music
  if (urlLower.includes('music.youtube.com')) {
    const videoId = url.match(/[?&]v=([^&]+)/)?.[1];
    return { 
      type: 'youtube_music', 
      embedUrl: videoId ? `https://www.youtube.com/embed/${videoId}` : null,
      videoId,
      originalUrl: url
    };
  }
  
  // TikTok
  if (urlLower.includes('tiktok.com')) {
    return { 
      type: 'tiktok', 
      originalUrl: url 
    };
  }
  
  // SoundCloud
  if (urlLower.includes('soundcloud.com')) {
    return { 
      type: 'soundcloud', 
      embedUrl: `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&color=%23ff5500&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false`,
      originalUrl: url 
    };
  }
  
  // Facebook Video or Post
  if (urlLower.includes('facebook.com')) {
    const isVideo = urlLower.includes('/videos/') || urlLower.includes('/watch') || urlLower.includes('/reel');
    return { 
      type: isVideo ? 'facebook_video' : 'facebook', 
      originalUrl: url 
    };
  }
  
  // Instagram
  if (urlLower.includes('instagram.com')) {
    return { 
      type: 'instagram', 
      originalUrl: url 
    };
  }
  
  // Spotify
  if (urlLower.includes('spotify.com')) {
    const match = url.match(/spotify\.com\/(track|album|playlist|episode)\/([^?]+)/);
    if (match) {
      return { 
        type: 'spotify', 
        embedUrl: `https://open.spotify.com/embed/${match[1]}/${match[2]}`,
        contentType: match[1],
        contentId: match[2],
        originalUrl: url
      };
    }
  }
  
  // Twitter/X
  if (urlLower.includes('twitter.com') || urlLower.includes('x.com')) {
    return { 
      type: 'twitter', 
      originalUrl: url 
    };
  }
  
  // Direct image URLs
  if (urlLower.match(/\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?.*)?$/i)) {
    return { type: 'image', embedUrl: url, originalUrl: url };
  }
  
  // Direct video URLs
  if (urlLower.match(/\.(mp4|webm|ogg|mov)(\?.*)?$/i)) {
    return { type: 'video', embedUrl: url, originalUrl: url };
  }
  
  // Default: try as image
  return { type: 'image', embedUrl: url, originalUrl: url };
};

// Media Preview Component using react-social-media-embed
const MediaPreview = ({ url, mediaInfo }) => {
  if (!url || !mediaInfo) return null;
  
  const { type, embedUrl } = mediaInfo;
  
  switch (type) {
    case 'youtube':
    case 'youtube_music':
      return (
        <div className="aspect-video rounded-xl overflow-hidden bg-black">
          <iframe
            src={embedUrl}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title="YouTube video"
          />
        </div>
      );
    
    case 'tiktok':
      return (
        <div className="rounded-xl overflow-hidden flex justify-center bg-gray-900 py-4">
          <TikTokEmbed url={url} width={325} />
        </div>
      );
    
    case 'soundcloud':
      return (
        <div className="rounded-xl overflow-hidden">
          <iframe
            src={embedUrl}
            className="w-full"
            height="166"
            allow="autoplay"
            title="SoundCloud"
          />
        </div>
      );
    
    case 'spotify':
      return (
        <div className="rounded-xl overflow-hidden">
          <iframe
            src={embedUrl}
            className="w-full"
            height="352"
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            title="Spotify"
          />
        </div>
      );
    
    case 'facebook':
    case 'facebook_video':
      return (
        <div className="rounded-xl overflow-hidden flex justify-center py-4">
          <FacebookEmbed url={url} width={500} />
        </div>
      );
    
    case 'instagram':
      return (
        <div className="rounded-xl overflow-hidden flex justify-center py-4">
          <InstagramEmbed url={url} width={400} />
        </div>
      );
    
    case 'video':
      return (
        <div className="rounded-xl overflow-hidden bg-black">
          <video
            src={url}
            controls
            className="w-full max-h-96"
          />
        </div>
      );
    
    case 'image':
    default:
      return (
        <div className="rounded-xl overflow-hidden border border-white/10">
          <img
            src={url}
            alt="Preview"
            className="w-full max-h-96 object-cover"
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/400x300?text=Invalid+URL';
            }}
          />
        </div>
      );
  }
};

// Get icon for media type
const getMediaIcon = (type) => {
  switch (type) {
    case 'youtube':
    case 'youtube_music':
    case 'tiktok':
    case 'facebook_video':
    case 'video':
      return <Play className="h-4 w-4" />;
    case 'soundcloud':
    case 'spotify':
      return <Music className="h-4 w-4" />;
    default:
      return <ImageIcon className="h-4 w-4" />;
  }
};

// Get label for media type
const getMediaLabel = (type) => {
  const labels = {
    youtube: 'YouTube Video',
    youtube_music: 'YouTube Music',
    tiktok: 'TikTok Video',
    soundcloud: 'SoundCloud',
    spotify: 'Spotify',
    facebook: 'Facebook Post',
    facebook_video: 'Facebook Video',
    instagram: 'Instagram',
    twitter: 'Twitter/X',
    video: 'Video',
    image: 'Image',
    unknown: 'Unknown'
  };
  return labels[type] || 'Media';
};

function CreatePost() {
  const [user, setUser] = useState(null);
  const [uploadMethod, setUploadMethod] = useState('url');
  const [mediaUrl, setMediaUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [mediaInfo, setMediaInfo] = useState(null);
  const [caption, setCaption] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, { credentials: 'include' });
      const data = await response.json();
      
      if (!response.ok) {
        toast.error('Please login to create posts');
        navigate('/login');
        return;
      }
      
      setUser(data);
    } catch (error) {
      toast.error('Please login to create posts');
      navigate('/login');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
        setMediaInfo({ type: 'image', embedUrl: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const triggerFileInput = () => {
    document.getElementById('file-upload').click();
  };

  const handleUrlChange = (e) => {
    const url = e.target.value;
    setMediaUrl(url);
    
    if (url.trim()) {
      const info = detectMediaType(url);
      setMediaInfo(info);
      setPreviewUrl(url);
    } else {
      setMediaInfo(null);
      setPreviewUrl('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      let finalMediaUrl = '';
      let contentType = 'image';

      if (uploadMethod === 'url') {
        finalMediaUrl = mediaUrl;
        if (!finalMediaUrl) {
          throw new Error('Please provide a media URL');
        }
        contentType = mediaInfo?.type || 'image';
      } else if (uploadMethod === 'file') {
        if (!previewUrl) {
          throw new Error('Please select an image file');
        }
        finalMediaUrl = previewUrl;
        contentType = 'image';
      }

      const response = await fetch(`${API}/posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          content_type: contentType,
          media_url: finalMediaUrl,
          caption: caption
        })
      });

      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error('Invalid response from server');
      }

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create post');
      }

      toast.success('Post created successfully!');
      navigate('/feed');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen pb-24">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4" data-testid="back-btn">
          ← Back
        </Button>

        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-2 tracking-tight">Create Post</h1>
          <p className="text-muted-foreground">Share your talent with the world</p>
        </div>

        <Card className="glass-panel p-6 md:p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="text-sm font-medium mb-2 block">Upload Method</label>
              <div className="flex gap-4">
                <Button
                  type="button"
                  variant={uploadMethod === 'url' ? 'default' : 'outline'}
                  onClick={() => { setUploadMethod('url'); setPreviewUrl(mediaUrl); if (mediaUrl) setMediaInfo(detectMediaType(mediaUrl)); }}
                  className="flex-1 rounded-full"
                  data-testid="upload-method-url"
                >
                  <LinkIcon className="mr-2 h-4 w-4" />
                  Media URL
                </Button>
                <Button
                  type="button"
                  variant={uploadMethod === 'file' ? 'default' : 'outline'}
                  onClick={() => { setUploadMethod('file'); setPreviewUrl(''); setMediaInfo(null); }}
                  className="flex-1 rounded-full"
                  data-testid="upload-method-file"
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Upload File
                </Button>
              </div>
            </div>

            {uploadMethod === 'url' && (
              <div>
                <label className="text-sm font-medium mb-2 block">Media URL</label>
                <div className="relative">
                  <LinkIcon className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                  <Input
                    type="url"
                    value={mediaUrl}
                    onChange={handleUrlChange}
                    className="pl-10 bg-white/5 border-white/10 focus:border-primary h-12 rounded-xl"
                    placeholder="Paste YouTube, TikTok, SoundCloud, Spotify, or image URL..."
                    required={uploadMethod === 'url'}
                    data-testid="post-media-url-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Supports: YouTube, TikTok, SoundCloud, Spotify, Facebook, Instagram, and direct image/video URLs
                </p>
                
                {/* Media Type Badge */}
                {mediaInfo && mediaInfo.type !== 'unknown' && (
                  <div className="mt-3 flex items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary/20 text-primary rounded-full text-sm">
                      {getMediaIcon(mediaInfo.type)}
                      {getMediaLabel(mediaInfo.type)} detected
                    </span>
                  </div>
                )}
              </div>
            )}

            {uploadMethod === 'file' && (
              <div>
                <label className="text-sm font-medium mb-2 block">Upload Image</label>
                <div className="border-2 border-dashed border-white/20 rounded-xl p-8 text-center hover:border-primary/50 transition-colors">
                  <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                    id="file-upload"
                    data-testid="file-upload-input"
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="rounded-full"
                    onClick={triggerFileInput}
                    data-testid="choose-file-btn"
                  >
                    Choose Image
                  </Button>
                  {selectedFile && (
                    <p className="mt-4 text-sm text-secondary font-medium">{selectedFile.name}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-2">Max file size: 10MB</p>
                </div>
              </div>
            )}

            <div>
              <label className="text-sm font-medium mb-2 block">Caption (Optional)</label>
              <Textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                className="bg-white/5 border-white/10 focus:border-primary rounded-xl min-h-[120px]"
                placeholder="Write a caption for your post..."
                data-testid="post-caption-input"
              />
            </div>

            {/* Preview Section */}
            {previewUrl && (
              <div>
                <label className="text-sm font-medium mb-2 block">Preview</label>
                <MediaPreview url={previewUrl} mediaInfo={mediaInfo} />
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-primary hover:scale-105 transition-transform rounded-full py-6 font-bold"
              disabled={isLoading || (!mediaUrl && !previewUrl)}
              data-testid="create-post-submit-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Creating Post...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-5 w-5" />
                  Create Post
                </>
              )}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}

export default CreatePost;
